// src/components/TicketQueue.js
import React, { useState, useMemo } from 'react';
import { FiClock, FiChevronRight, FiChevronDown } from 'react-icons/fi';
import moment from 'moment';

// Helper: Check Sub-ticket Activity
const checkSubTicketActivity = (parentTicketId, allTickets) => {
    if (!allTickets || !parentTicketId) return false;
    return allTickets.some(t => {
        const pId = typeof t.parentId === 'object' ? t.parentId?._id : t.parentId;
        return pId === parentTicketId && t.activeCheckIns && t.activeCheckIns.length > 0;
    });
};

// Helper: Safe ID Comparison
const areIdsEqual = (id1, id2) => {
    if (!id1 || !id2) return false;
    const s1 = typeof id1 === 'object' ? id1.toString() : id1;
    const s2 = typeof id2 === 'object' ? id2.toString() : id2;
    return s1 === s2;
};

const TicketQueue = ({ 
    tickets = [],           
    allTickets = [],        
    appointments = [],      
    onTicketClick,          
    highlightTechnicianId,  
    emptyMessage = "No tickets in queue.",
    hideClient = false // ✅ NEW: Prop to optionally hide client name
}) => {
    const [expandedTickets, setExpandedTickets] = useState(new Set());

    const toggleExpansion = (e, ticketId) => {
        e.stopPropagation();
        setExpandedTickets(prev => {
            const next = new Set(prev);
            if (next.has(ticketId)) next.delete(ticketId);
            else next.add(ticketId);
            return next;
        });
    };

    const ticketScheduleMap = useMemo(() => {
        const map = new Map();
        if (!appointments) return map;
        const now = new Date();
        appointments
            .filter(app => new Date(app.startTime) > now)
            .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
            .forEach(app => {
                const ticketId = typeof app.ticketId === 'object' ? app.ticketId._id : app.ticketId;
                if (ticketId && !map.has(ticketId)) {
                    map.set(ticketId, new Date(app.startTime));
                }
            });
        return map;
    }, [appointments]);

    if (!tickets || tickets.length === 0) {
        return (
            <div className="no-tasks-message">
                <p>{emptyMessage}</p>
            </div>
        );
    }

    return (
        <div className="tasks-list">
            {tickets.map(ticket => {
                const scheduledDate = ticketScheduleMap.get(ticket._id);
                const isInProgress = ticket.status === 'In Progress';
                
                const isCheckedIn = highlightTechnicianId && ticket.activeCheckIns?.some(ci => areIdsEqual(ci.technicianId, highlightTechnicianId));

                const childTickets = allTickets.filter(t => {
                    const pId = typeof t.parentId === 'object' ? t.parentId?._id : t.parentId;
                    return pId === ticket._id;
                }).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

                const hasChildren = childTickets.length > 0;
                const isExpanded = expandedTickets.has(ticket._id);
                const subTicketActive = checkSubTicketActivity(ticket._id, allTickets);

                return (
                    <React.Fragment key={ticket._id}>
                        {/* Parent Ticket Row */}
                        <div 
                            className={`task-item border-${ticket.priority.toLowerCase()} clickable-row ${isCheckedIn ? 'checked-in-by-me' : (isInProgress ? 'status-in-progress-row' : '')}`} 
                            onClick={() => onTicketClick(ticket._id)}
                        >
                            {hasChildren && (
                                <button
                                    className="sub-ticket-toggle"
                                    onClick={(e) => toggleExpansion(e, ticket._id)}
                                    style={{ marginRight: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', display: 'flex', alignItems: 'center', padding: '4px' }}
                                >
                                    {isExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                                </button>
                            )}

                            <div className="task-info">
                                <p className="task-title">
                                    {isCheckedIn && <span className="active-timer-dot" title="You are clocked in"></span>}
                                    {ticket.subject}
                                </p>
                                
                                <div className="task-meta" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                                    {/* ✅ CONDITIONAL: Only show client if not hidden */}
                                    {!hideClient && ticket.client && <span>{ticket.client} •</span>}
                                    
                                    {isCheckedIn ? (
                                        <strong style={{color: 'var(--green-700)'}}>
                                            YOU ARE CHECKED IN
                                        </strong>
                                    ) : (
                                        <>
                                            {subTicketActive && !isInProgress && (
                                                <span className="status-badge status-sub-ticket-active" title="A sub-ticket is currently in progress" style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem' }}>
                                                    Sub Active
                                                </span>
                                            )}
                                            <span>{(scheduledDate ? `Next Appt: ${moment(scheduledDate).format('ddd, MMM D @ h:mm A')}` : `Status: ${ticket.status}`)}</span>
                                        </>
                                    )}
                                </div>
                            </div>
                            
                            <div className="task-actions">
                                {isCheckedIn && <FiClock className="pulse-icon" style={{color: 'var(--green-600)', marginRight: '0.5rem'}} />}
                                <span className={`tag priority-${ticket.priority.toLowerCase()}`}>{ticket.priority}</span>
                                <button className="btn btn-action" onClick={(e) => { e.stopPropagation(); onTicketClick(ticket._id); }}>View Ticket</button>
                            </div>
                        </div>

                        {/* Expanded Sub-Tickets */}
                        {isExpanded && childTickets.map(child => {
                            const isChildCheckedIn = highlightTechnicianId && child.activeCheckIns?.some(ci => areIdsEqual(ci.technicianId, highlightTechnicianId));
                            const isChildInProgress = child.status === 'In Progress';

                            const rowClass = `task-item border-${child.priority.toLowerCase()} clickable-row sub-ticket-row ${isChildCheckedIn ? 'checked-in-by-me' : (isChildInProgress ? 'status-in-progress-row' : '')}`;
                            const borderStyle = isChildCheckedIn ? '#16a34a' : (isChildInProgress ? '#ea580c' : 'transparent');

                            return (
                                <div 
                                    key={child._id} 
                                    className={rowClass}
                                    style={{ 
                                        marginLeft: '2rem', 
                                        fontSize: '0.9em', 
                                        borderLeftWidth: '4px',
                                        borderLeftColor: borderStyle,
                                        marginRight: '0.5rem'
                                    }}
                                    onClick={() => onTicketClick(child._id)}
                                >
                                    <div style={{ marginRight: '0.5rem', color: 'var(--gray-400)', display: 'flex', alignItems: 'center' }}>└</div>
                                    <div className="task-info">
                                        <p className="task-title">
                                            {isChildCheckedIn && <span className="active-timer-dot" title="You are clocked in"></span>}
                                            {child.subject}
                                        </p>
                                        <p className="task-meta">
                                            {isChildCheckedIn ? (
                                                <strong style={{color: 'var(--green-700)'}}>
                                                    YOU ARE CHECKED IN
                                                </strong>
                                            ) : (
                                                `Status: ${child.status}`
                                            )}
                                        </p>
                                    </div>
                                    <div className="task-actions">
                                        {isChildCheckedIn && <FiClock className="pulse-icon" style={{color: 'var(--green-600)', marginRight: '0.5rem'}} />}
                                        <span className={`tag priority-${child.priority.toLowerCase()}`}>{child.priority}</span>
                                        <button className="btn btn-action" onClick={(e) => { e.stopPropagation(); onTicketClick(child._id); }}>View Ticket</button>
                                    </div>
                                </div>
                            );
                        })}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default TicketQueue;